import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { apiJson } from "./api";
import { AdminEventsDataProvider } from "./admin-events-data-context";
import { AdminSharedMonthCalendar } from "./AdminSharedMonthCalendar";
import { AdminFocusDayBar } from "./AdminFocusDayBar";
import { AdminQuickSearchChips } from "./AdminQuickSearchChips";
import {
  ADMIN_KARIN_SUBTYPE_FILTER_OPTIONS,
  buildAdminMonthChoices,
  jstCurrentYearMonth,
  lastDayOfMonthYmd,
  parseAdminFocusDay,
  parseAdminKarinSubtypeFilter,
  type AdminKarinSubtypeFilter,
  type AdminOutletFilter,
  type AdminVisibilityFilter,
} from "./admin-list-filter";

function chipClass({ isActive }: { isActive: boolean }): string {
  return `chip${isActive ? " active" : ""}`;
}

type AdminTopStats = {
  noticeManagingVisible: number;
  noticeManagingHidden: number;
  promoting: number;
  unreleased: number;
  sitePending: number;
  siteFailed: number;
  unposted: number;
};

export function AdminWorkspace() {
  const [topStats, setTopStats] = useState<AdminTopStats | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterTab, setFilterTab] = useState<"dates" | "keyword">("dates");

  const monthParam = (searchParams.get("month") ?? "").trim();
  const evFromParam = (searchParams.get("ev_from") ?? "").trim();
  const evToParam = (searchParams.get("ev_to") ?? "").trim();
  const hasDateRange = Boolean(evFromParam || evToParam);

  let adminEventDateFrom = evFromParam;
  let adminEventDateTo = evToParam;
  if (!hasDateRange && monthParam && /^\d{4}-\d{2}$/.test(monthParam)) {
    adminEventDateFrom = adminEventDateFrom || `${monthParam}-01`;
    adminEventDateTo = adminEventDateTo || lastDayOfMonthYmd(monthParam);
  }

  const monthChoices = useMemo(() => buildAdminMonthChoices(), []);

  const adminKeyword = searchParams.get("q") ?? "";
  const adminVisRaw = searchParams.get("vis") ?? "";
  const adminVis: AdminVisibilityFilter =
    adminVisRaw === "unreleased" || adminVisRaw === "public" ? adminVisRaw : "";
  const adminKarinSubtype = parseAdminKarinSubtypeFilter(searchParams.get("karin_type"));
  const adminFocusDay = parseAdminFocusDay(searchParams.get("focus_day"));

  /** 旧URL（ev_from=ev_to=同日）→ focus_day へ移行（一覧を1日だけに絞らない） */
  useEffect(() => {
    const from = evFromParam;
    const to = evToParam;
    if (!from || from !== to || !/^\d{4}-\d{2}-\d{2}$/.test(from)) return;
    if (searchParams.get("focus_day")) return;
    const next = new URLSearchParams(searchParams);
    next.set("focus_day", from);
    next.delete("ev_from");
    next.delete("ev_to");
    if (!monthParam) next.set("month", from.slice(0, 7));
    setSearchParams(next, { replace: true });
  }, [evFromParam, evToParam, monthParam, searchParams, setSearchParams]);

  /** 予定管理：既定は「今月 + 公開のみ」。表サイトに出るカレンダー確認を主目的にする（アーカイブは除外） */
  useEffect(() => {
    const path = location.pathname;
    if (!path.startsWith("/admin/events")) return;
    if (path.endsWith("/archive")) return;
    if ((monthParam || evFromParam || evToParam) && adminVisRaw) return;
    const next = new URLSearchParams(searchParams);
    if (!monthParam && !evFromParam && !evToParam) next.set("month", jstCurrentYearMonth());
    if (!adminVisRaw) next.set("vis", "public");
    setSearchParams(next, { replace: true });
  }, [location.pathname, monthParam, evFromParam, evToParam, adminVisRaw, searchParams, setSearchParams]);

  const mergeAdminQuery = (path: string) => {
    const next = new URLSearchParams();
    const m = monthParam.trim();
    if (m && /^\d{4}-\d{2}$/.test(m) && !evFromParam && !evToParam) {
      next.set("month", m);
    } else {
      if (evFromParam) next.set("ev_from", evFromParam);
      if (evToParam) next.set("ev_to", evToParam);
    }
    if (adminKeyword.trim()) next.set("q", adminKeyword.trim());
    if (adminVis) next.set("vis", adminVis);
    if (adminKarinSubtype !== "all") next.set("karin_type", adminKarinSubtype);
    if (adminFocusDay) next.set("focus_day", adminFocusDay);
    const qs = next.toString();
    return qs ? `${path}?${qs}` : path;
  };

  const setEventDateFromParam = (v: string) => {
    const next = new URLSearchParams(searchParams);
    next.delete("focus_day");
    next.delete("month");
    const t = v.trim();
    if (t) next.set("ev_from", t);
    else next.delete("ev_from");
    setSearchParams(next, { replace: true });
  };

  const setEventDateToParam = (v: string) => {
    const next = new URLSearchParams(searchParams);
    next.delete("focus_day");
    next.delete("month");
    const t = v.trim();
    if (t) next.set("ev_to", t);
    else next.delete("ev_to");
    setSearchParams(next, { replace: true });
  };

  const setEventMonthOnlyParam = (v: string) => {
    const next = new URLSearchParams(searchParams);
    next.delete("focus_day");
    next.delete("ev_from");
    next.delete("ev_to");
    const t = v.trim();
    if (t && /^\d{4}-\d{2}$/.test(t)) next.set("month", t);
    else next.delete("month");
    setSearchParams(next, { replace: true });
  };

  const setKeywordParam = (v: string) => {
    const next = new URLSearchParams(searchParams);
    const t = v.trim();
    if (t) next.set("q", t);
    else next.delete("q");
    setSearchParams(next, { replace: true });
  };

  const navigateEventModeWithKeyword = (path: string, keyword: string) => {
    const next = new URLSearchParams();
    const m = monthParam.trim();
    if (m && /^\d{4}-\d{2}$/.test(m) && !evFromParam && !evToParam) {
      next.set("month", m);
    } else {
      if (evFromParam) next.set("ev_from", evFromParam);
      if (evToParam) next.set("ev_to", evToParam);
    }
    const t = keyword.trim();
    if (t) next.set("q", t);
    if (adminVis) next.set("vis", adminVis);
    if (adminFocusDay) next.set("focus_day", adminFocusDay);
    const qs = next.toString();
    navigate(qs ? `${path}?${qs}` : path);
  };

  const setVisParam = (v: AdminVisibilityFilter) => {
    const next = new URLSearchParams(searchParams);
    if (!v) next.delete("vis");
    else next.set("vis", v);
    setSearchParams(next, { replace: true });
  };

  const setKarinSubtypeParam = (v: AdminKarinSubtypeFilter) => {
    const next = new URLSearchParams(searchParams);
    if (v === "all") next.delete("karin_type");
    else next.set("karin_type", v);
    setSearchParams(next, { replace: true });
  };

  const clearFilters = () => {
    if (location.pathname.startsWith("/admin/events") && !location.pathname.endsWith("/archive")) {
      setSearchParams({ month: jstCurrentYearMonth(), vis: "public" }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const outletCtx: AdminOutletFilter = {
    adminEventDateFrom: adminEventDateFrom.trim(),
    adminEventDateTo: adminEventDateTo.trim(),
    adminKeyword: adminKeyword.trim(),
    adminVis,
    adminKarinSubtype,
  };

  const karinSubtypeActiveLabel =
    ADMIN_KARIN_SUBTYPE_FILTER_OPTIONS.find((o) => o.value === adminKarinSubtype)?.label ?? "";

  const monthOnlyActive = Boolean(monthParam && /^\d{4}-\d{2}$/.test(monthParam) && !hasDateRange);
  const dateFilterActive = Boolean(adminEventDateFrom.trim() || adminEventDateTo.trim());

  const onEventsPage = location.pathname.startsWith("/admin/events");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const j = await apiJson<{ stats?: AdminTopStats }>("/api/admin/announcements");
        if (alive && j.stats) setTopStats(j.stats);
      } catch {
        /* optional */
      }
    })();
    return () => {
      alive = false;
    };
  }, [location.pathname]);

  return (
    <div className={`admin-shell${adminFocusDay && onEventsPage ? " admin-shell--focus-day-nav" : ""}`}>
      <header className="topbar">
        <div className="brand">
          <h1>管理メニュー</h1>
          <p>自動取得は廃止しました。宮本佳林の予定は管理画面から手入力し、その他の予定は表サイトの投稿フォームから受け付けます。</p>
        </div>
        <div className="row" style={{ flexWrap: "wrap", gap: 8 }}>
          <Link className="pill" to="/">
            表サイト
          </Link>
        </div>
      </header>

      {topStats ? (
        <div className="na-admin-stats" style={{ marginBottom: 10 }}>
          <span className="na-admin-stat-pill">お知らせ {topStats.noticeManagingVisible}</span>
          <span className="na-admin-stat-pill">告知中 {topStats.promoting}</span>
          <span className="na-admin-stat-pill">未解禁 {topStats.unreleased}</span>
          <span className="na-admin-stat-pill">反映待ち {topStats.sitePending}</span>
          <span className="na-admin-stat-pill">反映失敗 {topStats.siteFailed}</span>
        </div>
      ) : null}

      <AdminEventsDataProvider>
        <AdminSharedMonthCalendar outletFilter={outletCtx} />

      <details className="card admin-filter-card admin-filter-collapse">
        <summary className="admin-filter-collapse-summary">
          <span style={{ fontWeight: 700, fontSize: 14 }}>リスト絞り込み</span>
          <span className="mono admin-filter-collapse-hint">開いて条件を変更</span>
        </summary>
        <div className="row" style={{ justifyContent: "flex-end", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
          <button type="button" className="btn small secondary" onClick={clearFilters}>
            条件クリア
          </button>
        </div>
        <details className="admin-filter-help-details">
          <summary>絞り込みの説明</summary>
          <p className="admin-filter-help-text">
            <strong>開催日</strong>は <code>events.event_date</code> の日付で、<strong>開始〜終了</strong>の範囲か、<strong>開催月だけ</strong>のどちらかで絞れます（月を選ぶとその月1日〜末日が自動で入ります）。日付を手で入れると月選択は外れます。
            予定の各タブでは、<strong>開催日の条件が空</strong>のとき自動で<strong>今月（日本時間）</strong>が入り、公開状態は既定で<strong>公開のみ</strong>になります（表サイトのカレンダー確認用）。アーカイブ一覧だけは月を自動では付けません。下の<strong>出演者・グループ・佳林タイプ</strong>は表サイトと同じクイック絞り込みです。キーワード欄と連動します。公開状態は{" "}
            <code>visibility</code> で絞ります。
          </p>
        </details>
        <div className="row" style={{ gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--muted)" }}>公開状態</span>
          <button type="button" className={`chip${adminVis === "" ? " active" : ""}`} onClick={() => setVisParam("")}>
            すべて
          </button>
          <button type="button" className={`chip${adminVis === "unreleased" ? " active" : ""}`} onClick={() => setVisParam("unreleased")}>
            非公開のみ
          </button>
          <button type="button" className={`chip${adminVis === "public" ? " active" : ""}`} onClick={() => setVisParam("public")}>
            公開のみ
          </button>
        </div>
        <div className="row" style={{ gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            className={`chip${filterTab === "dates" ? " active" : ""}`}
            onClick={() => setFilterTab("dates")}
          >
            開催日
          </button>
          <button
            type="button"
            className={`chip${filterTab === "keyword" ? " active" : ""}`}
            onClick={() => setFilterTab("keyword")}
          >
            キーワード
          </button>
        </div>
        {filterTab === "dates" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: 280 }}>
              <span className="mono" style={{ fontSize: 11, color: "var(--muted)" }}>
                開催月だけ（YYYY-MM）
              </span>
              <select
                className="input"
                value={monthOnlyActive ? monthParam : ""}
                onChange={(e) => setEventMonthOnlyParam(e.target.value)}
              >
                <option value="">指定なし</option>
                {monthChoices.map((ym) => (
                  <option key={ym} value={ym}>
                    {ym}
                  </option>
                ))}
              </select>
            </label>
            <div className="row" style={{ gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
              <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 160 }}>
                <span className="mono" style={{ fontSize: 11, color: "var(--muted)" }}>
                  開催日（開始）
                </span>
                <input
                  className="input"
                  type="date"
                  value={hasDateRange ? evFromParam : ""}
                  onChange={(e) => setEventDateFromParam(e.target.value)}
                />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 160 }}>
                <span className="mono" style={{ fontSize: 11, color: "var(--muted)" }}>
                  開催日（終了）
                </span>
                <input
                  className="input"
                  type="date"
                  value={hasDateRange ? evToParam : ""}
                  onChange={(e) => setEventDateToParam(e.target.value)}
                />
              </label>
            </div>
          </div>
        ) : (
          <label style={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: 480 }}>
            <span className="mono" style={{ fontSize: 11, color: "var(--muted)" }}>
              キーワード検索
            </span>
            <input
              className="input"
              value={adminKeyword}
              placeholder="イベント名・会場・本番メモ・URL など"
              onChange={(e) => setKeywordParam(e.target.value)}
            />
          </label>
        )}
        <AdminQuickSearchChips
          keyword={adminKeyword}
          karinSubtype={adminKarinSubtype}
          onKeyword={setKeywordParam}
          onKarinSubtype={setKarinSubtypeParam}
          onPersonQuickPick={(name, nextKeyword) => {
            navigateEventModeWithKeyword(name === "宮本佳林" ? "/admin/events/karin" : "/admin/events/jp_room", nextKeyword);
          }}
          onGroupQuickPick={(_label, nextKeyword) => {
            navigateEventModeWithKeyword("/admin/events/helloproject", nextKeyword);
          }}
        />
        {(dateFilterActive || adminKeyword.trim() || adminVis || adminKarinSubtype !== "all" || adminFocusDay) && (
          <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "var(--muted)" }}>適用中:</span>
            {dateFilterActive ? (
              <span className="pill mono" style={{ fontSize: 12 }}>
                {monthOnlyActive ? `開催月 ${monthParam}` : `開催日 ${adminEventDateFrom.trim() || "…"}〜${adminEventDateTo.trim() || "…"}`}
              </span>
            ) : null}
            {adminKeyword.trim() ? (
              <span className="pill mono" style={{ fontSize: 12 }}>
                語 {adminKeyword.trim().slice(0, 48)}
                {adminKeyword.trim().length > 48 ? "…" : ""}
              </span>
            ) : null}
            {adminVis === "unreleased" ? (
              <span className="pill mono" style={{ fontSize: 12 }}>
                非公開のみ
              </span>
            ) : null}
            {adminVis === "public" ? (
              <span className="pill mono" style={{ fontSize: 12 }}>
                公開のみ
              </span>
            ) : null}
            {adminKarinSubtype !== "all" ? (
              <span className="pill mono" style={{ fontSize: 12 }}>
                佳林タイプ {karinSubtypeActiveLabel}
              </span>
            ) : null}
            {adminFocusDay ? (
              <span className="pill mono" style={{ fontSize: 12 }}>
                表示日 {adminFocusDay}
              </span>
            ) : null}
          </div>
        )}
      </details>

      <div className="admin-nav">
        <NavLink className={chipClass} to={mergeAdminQuery("/admin/announcements")} end>
          お知らせ管理
        </NavLink>
        <NavLink className={chipClass} to="/admin/visitor-requests" end>
          こんなイベントもあるよ！投稿
        </NavLink>
        <NavLink className={chipClass} to="/admin/correction-reports" end>
          間違ってるよ！報告
        </NavLink>
        <NavLink className={chipClass} to={mergeAdminQuery("/admin/events/all")} end>
          全ての予定を表示
        </NavLink>
        <NavLink className={chipClass} to={mergeAdminQuery("/admin/events/karin")} end>
          宮本佳林予定管理
        </NavLink>
        <NavLink className={chipClass} to={mergeAdminQuery("/admin/events/jp_room")} end>
          J.P ROOM予定管理
        </NavLink>
        <NavLink className={chipClass} to={mergeAdminQuery("/admin/events/helloproject")} end>
          H!P予定管理
        </NavLink>
        <NavLink className={chipClass} to={mergeAdminQuery("/admin/events/archive")} end>
          アーカイブ一覧
        </NavLink>
        <NavLink className={chipClass} to={mergeAdminQuery("/admin/settings")} end>
          設定
        </NavLink>
      </div>

      <div style={{ marginTop: 8 }}>
        <Outlet context={outletCtx} />
      </div>

      </AdminEventsDataProvider>

      <AdminFocusDayBar />

      <section className="card" style={{ marginTop: 24 }}>
        <h2 style={{ marginTop: 0, fontSize: 18 }}>運用方針</h2>
        <p style={{ marginTop: 8, marginBottom: 0, color: "var(--muted)", lineHeight: 1.7, fontSize: 13 }}>
          公式サイトからの自動取得・同期は使いません。宮本佳林の予定は管理側で登録し、ファン投稿は表サイトから即公開されたあと管理側で修正できます。
        </p>
      </section>
    </div>
  );
}
